// A1-104 (operator 2026-10-31) — the two facts that decide where a sale is
// taxed, answered on the card: "Where does the customer belong?" (a business
// customer is taxed where it belongs, HMRC Notice 741A s.6.3) and "What was
// sold?" (an admission to an event where it is held, s.9.2). Saved at once
// and applied by the next re-run, like every Step 4 answer (SavedAnswersBar);
// the uploaded figures never change. Offered by the server only (answers.js).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { CUSTOMER_COUNTRY_REASONS, SOLD_AS_REASONS } from "../../lib/reviewReasonPresets";
import { ALL_COUNTRY_CHOICES, countryOptionLabel } from "../../lib/countries";

export const NUMBER = "NUMBER";
const SELECT = "w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs";
const TRIGGER = "text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800";

// One popover for both questions: a choice, a reason, Save.
const Ask = ({ testid, label, question, children, valid, presets, onSave, calcFrozen }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (calcFrozen) return null;
  const ok = valid && reason.trim().length >= 8;
  const save = async () => {
    if (!ok || busy) return;
    setBusy(true); setErr(null);
    try {
      await onSave(reason.trim());
      setOpen(false); setReason(""); setPreset("");
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setErr(null); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-open`} className={TRIGGER}>{label}</button>
      </PopoverTrigger>
      <PopoverContent data-testid={`${testid}-popover`}
                      className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2">
        <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{question}</div>
        {children}
        <PresetReasonSelect testid={`${testid}-reason`} presets={presets} preset={preset}
                            onPick={(p, v) => { setPreset(p); setReason(v); }} />
        <div className="text-[11px] text-slate-600 dark:text-slate-300">
          {t("ui.review_answers.customer_applies", { defaultValue: "Figures change at the next re-run." })}
        </div>
        {err && <div role="alert" data-testid={`${testid}-error`} className="text-xs text-red-700 dark:text-red-300">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setOpen(false)} className="text-xs underline text-slate-600 dark:text-slate-300">
            {t("ui.review_grouped.sc_cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" data-testid={`${testid}-save`} disabled={busy || !ok} onClick={save} aria-busy={busy}
                  className="text-xs font-semibold px-3 py-1 rounded bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white">
            {busy ? t("ui.review_grouped.sc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_answers.customer_save", { defaultValue: "Save" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const rowsBody = (tids, allOpen, flagType) => (allOpen ? { all_open: true, flag_type: flagType } : { txn_ids: tids });

/** "Where does the customer belong?" — for one row, ticked rows, or every
 * open row of the card (`allOpen`). `numberCountry`: the row's VAT number's
 * country, offered first; for many rows "where each one's number is from". */
export const CustomerCountry = ({ tids = [], allOpen, flagType, numberCountry, current, calcId, calcFrozen, onResolved, label, testId }) => {
  const { t } = useTranslation();
  const many = allOpen || tids.length > 1;
  const byNumber = many || !!numberCountry;
  const [country, setCountry] = useState(byNumber ? NUMBER : "");
  const key = testId || `cc-${allOpen ? "all" : tids.length > 1 ? "ticked" : tids[0]}`;
  if (!allOpen && !tids.length) return null;
  const save = async (reason) => {
    const res = await authClient.post(`/runs/calc/${calcId}/customer-country`,
      { ...rowsBody(tids, allOpen, flagType), country, reason });
    toast.success(t("ui.review_answers.customer_saved", { n: res?.data?.answered ?? tids.length,
      defaultValue: "Saved for {{n}} row(s) — applied when the calculation re-runs" }));
    if (onResolved) await onResolved();
  };
  const question = t("ui.review_answers.customer_question", { defaultValue: "Where does the customer belong?" });
  return (
    <span className="inline-flex items-center gap-2">
      {current && (
        <span data-testid={`${key}-current`} className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t("ui.review_answers.customer_current", { country: current, defaultValue: "Customer in {{country}} — applied at the next re-run" })}
        </span>
      )}
      <Ask testid={key} calcFrozen={calcFrozen} question={question} valid={!!country}
           presets={CUSTOMER_COUNTRY_REASONS} onSave={save}
           label={label || t("ui.review_answers.customer_open", { defaultValue: "Where does the customer belong? →" })}>
        <select data-testid={`${key}-country`} value={country} aria-label={question}
                onChange={(e) => setCountry(e.target.value)} className={SELECT}>
          {!byNumber && <option value="">{t("ui.review_answers.location_pick", { defaultValue: "Pick a country…" })}</option>}
          {byNumber && (
            <option value={NUMBER}>
              {many ? t("ui.review_answers.customer_by_number_many", { defaultValue: "Where each row's VAT number is from" })
                : t("ui.review_answers.customer_by_number", { country: numberCountry, defaultValue: "Where its VAT number is from ({{country}})" })}
            </option>
          )}
          {/* the whole card goes by each row's own number only (one country for
              every row is refused, api/routes_review_customer) */}
          {!allOpen && ALL_COUNTRY_CHOICES.map((c) => <option key={c} value={c}>{countryOptionLabel(c)}</option>)}
        </select>
      </Ask>
    </span>
  );
};

const SOLD = [
  ["DIGITAL_SERVICE", "A digital or online service"],
  ["EVENT_ONLINE", "An online event (attended remotely)"],
  ["EVENT_IN_PERSON", "An in-person event"],
];

/** "What was sold?" — one row or ticked rows; an in-person event names the
 * country it was held in. */
export const SoldAs = ({ tids = [], current, calcId, calcFrozen, onResolved }) => {
  const { t } = useTranslation();
  const [sold, setSold] = useState("");
  const [where, setWhere] = useState("");
  const key = `sold-${tids.length > 1 ? "ticked" : tids[0]}`;
  if (!tids.length) return null;
  const inPerson = sold === "EVENT_IN_PERSON";
  const save = async (reason) => {
    await authClient.post(`/runs/calc/${calcId}/sold-as`,
      { txn_ids: tids, sold_as: sold, ...(inPerson ? { event_country: where } : {}), reason });
    toast.success(t("ui.review_answers.customer_saved", { n: tids.length,
      defaultValue: "Saved for {{n}} row(s) — applied when the calculation re-runs" }));
    if (onResolved) await onResolved();
  };
  const question = t("ui.review_answers.sold_question", { defaultValue: "What was sold?" });
  const currentLabel = SOLD.find(([v]) => v === current)?.[1];
  return (
    <span className="inline-flex items-center gap-2">
      {currentLabel && (
        <span data-testid={`${key}-current`} className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t(`ui.review_answers.sold.${current}`, { defaultValue: currentLabel })}
        </span>
      )}
      <Ask testid={key} calcFrozen={calcFrozen} question={question} presets={SOLD_AS_REASONS} onSave={save}
           valid={!!sold && (!inPerson || /^[A-Z]{2}$/.test(where))}
           label={tids.length > 1
             ? t("ui.review_answers.sold_open_many", { n: tids.length, defaultValue: "What was sold, for {{n}} rows? →" })
             : t("ui.review_answers.sold_open", { defaultValue: "What was sold? →" })}>
        <select data-testid={`${key}-kind`} value={sold} aria-label={question}
                onChange={(e) => setSold(e.target.value)} className={SELECT}>
          <option value="">{t("ui.review_answers.sold_pick", { defaultValue: "Pick one…" })}</option>
          {SOLD.map(([v, l]) => <option key={v} value={v}>{t(`ui.review_answers.sold.${v}`, { defaultValue: l })}</option>)}
        </select>
        {inPerson && (
          <select data-testid={`${key}-where`} value={where} onChange={(e) => setWhere(e.target.value)} className={SELECT}
                  aria-label={t("ui.review_answers.location.event_country", { defaultValue: "Where did the event take place?" })}>
            <option value="">{t("ui.review_answers.location.event_country", { defaultValue: "Where did the event take place?" })}</option>
            {ALL_COUNTRY_CHOICES.map((c) => <option key={c} value={c}>{countryOptionLabel(c)}</option>)}
          </select>
        )}
      </Ask>
    </span>
  );
};

export default CustomerCountry;
